import { createBrowserRouter, Navigate, useParams, type RouteObject } from 'react-router-dom';
import { UserRole, OPERATIONS_ROLES, BUSINESS_SHEET_VIEW_ROLES } from '@bhops/shared';
import { AppShell } from '@/layout/AppShell';
import { ProtectedRoute } from '@/layout/ProtectedRoute';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { RobotsListPage } from '@/pages/RobotsListPage';
import { RobotDetailPage } from '@/pages/RobotDetailPage';
import { StoresPage } from '@/pages/StoresPage';
// docs/work_log/pdf-feedback-followups-2026-05-24 §StoreDetailPage —
// 매장 단일 상세 (PDF §Phase C 의 매장 ↔ 고객사/계약/배치 cross-link
// 의 매장 측 진입점).
import { StoreDetailPage } from '@/pages/stores/StoreDetailPage';
import { ContractsPage } from '@/pages/ContractsPage';
// FB 1차 배치 §3 (결정 A) — 계약 상세 페이지 미사용. `/contracts/:id` 는
// 고객사 상세로 redirect (ContractDetailRedirect). ContractDetailPage 컴포넌트
// 파일은 보존하되 라우터에서 직접 라우팅하지 않는다 (CustomersPage 등
// 다른 redirect-retired 페이지와 동일 패턴).
import { ContractDetailPage } from '@/pages/contracts/ContractDetailPage';
import { ContractCreateWizard } from '@/pages/contracts/ContractCreateWizard';
// docs/plan/customer-master-ux/plan.md — 'CustomersPage' (v1.0.14 §3 단일
// 뷰) 는 더 이상 라우팅에서 직접 사용되지 않음. /business/customers 는
// 새 통합 시트 (/business/master) 로 redirect. 컴포넌트 파일은 보존
// (Phase 2 follow-up 에서 detail 페이지의 일부 컴포넌트 재사용 후보).
import { CustomerDetailPage } from '@/pages/customers/CustomerDetailPage';
import { DealDetailPage } from '@/pages/deals/DealDetailPage';
import { DealsKanbanPage } from '@/pages/DealsKanbanPage';
import { DeploymentsPage } from '@/pages/DeploymentsPage';
// S3 Phase 3.1 — 분류 큐는 ServiceOrdersInboxPage 의 내부 탭(?view=triage)
// 으로 통합. ServiceOrdersTriagePage 는 이제 InboxPage 가 직접 import 해
// 탭 패널로 렌더하므로 라우터에서는 더 이상 직접 참조하지 않는다.
import { ServiceOrdersInboxPage } from '@/pages/ServiceOrdersInboxPage';
import { PrivacyRequestsInboxPage } from '@/pages/PrivacyRequestsInboxPage';
import { AuditLogsPage } from '@/pages/AuditLogsPage';
import { UsersPage } from '@/pages/UsersPage';
import { CustomerMasterPage } from '@/pages/business/CustomerMasterPage';
// docs/superpowers/plans/2026-05-29-s1-business-ux-comprehensive.md Phase 8 —
// 사이드바 '고객사 서류' 탭 (read-only). 필드팀 PWA 업로드 서류 표시.
import { CustomerDocumentsPage } from '@/pages/business/CustomerDocumentsPage';
import { DemoConversionPage } from '@/pages/business/DemoConversionPage';
import { InboundPage } from '@/pages/business/InboundPage';
import { InquiryPage } from '@/pages/business/InquiryPage';
import { InfrastructurePage } from '@/pages/InfrastructurePage';
import { FleetTrackerPage } from '@/pages/FleetTrackerPage';
// docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §6.3 — LOT 영향권 페이지.
// windows-ux-edit (2026-06, 슬라이드5) — 제조 사이드바 정리로 부품 카탈로그/부품
// LOT 리스트/공급사 nav + 라우트를 제거(D-4 비파괴: 페이지 컴포넌트·API·DB 보존).
// LOT 영향권(parts/lots/:id)은 로봇 상세 '부품' 탭에서 직접 링크되므로 라우트 유지.
// 오너 확정 2026-08-20 결정 4 — 제조팀 로봇 권한 요구(계획서 요구 1)로 세 화면
// 복원. 접근 역할 정본은 PARTS_SUPPLIERS_ROUTE_ROLES(permissions.ts) 하나 —
// 사이드바와 같은 상수를 공유한다.
import { PartLotImpactPage } from '@/pages/parts/PartLotImpactPage';
import { PartCatalogPage } from '@/pages/parts/PartCatalogPage';
import { PartLotsPage } from '@/pages/parts/PartLotsPage';
import { SuppliersPage } from '@/pages/suppliers/SuppliersPage';
import { PARTS_SUPPLIERS_ROUTE_ROLES, RETIRE_CANDIDATES_ROUTE_ROLES } from '@/permissions';
import { IncompleteRecordsPage } from '@/pages/admin/IncompleteRecordsPage';
import { OptionGroupsAdminPage } from '@/pages/admin/OptionGroupsAdminPage';
import { RetireCandidatesPage } from '@/pages/admin/RetireCandidatesPage';
import { SelfCheckLogsPage } from '@/pages/admin/SelfCheckLogsPage';
import { BusinessDashboardPage } from '@/pages/dashboard/BusinessDashboardPage';
import { ManufacturingDashboardPage } from '@/pages/dashboard/ManufacturingDashboardPage';
import { FieldDashboardPage } from '@/pages/dashboard/FieldDashboardPage';
import { ProductionPlanPage } from '@/pages/manufacturing/ProductionPlanPage';
import { AssignmentInboxPage } from '@/pages/manufacturing/AssignmentInboxPage';
// S2 Phase 2.3 — ECN(제조 변경점) 마스터 카탈로그 + 역추적.
import { EcnCatalogPage } from '@/pages/manufacturing/EcnCatalogPage';
// docs/plan/loader-history-traceability-2026-07 — 로더(로봇 팔) 이력·역추적.
import { LoadersListPage } from '@/pages/manufacturing/LoadersListPage';
import { LoaderQualityPage } from '@/pages/manufacturing/LoaderQualityPage';
import { LoaderRevsPage } from '@/pages/manufacturing/LoaderRevsPage';
import { LoaderDetailPage } from '@/pages/LoaderDetailPage';
// S2 Phase 2.10 — 재고 Kanban (시트4/5): 구분 사양별 stage 버킷 + Short SN.
import { InventoryKanbanPage } from '@/pages/manufacturing/InventoryKanbanPage';
// S3 Phase 3.2 — 설치 일정 탭은 인프라 발주(InfrastructurePage)의 우측
// 캘린더로 흡수됨. InstallCalendarPage 컴포넌트 자체는 InfrastructurePage
// 가 직접 렌더하므로 import 유지(라우터에서는 더 이상 라우트로 노출 안 함).
import { StoreMenuOfferingsPage } from '@/pages/StoreMenuOfferingsPage';
// S3 Phase 3.4 (spec §3.5) — 활성 고객사 관리 (운영 로봇 roster + 유지보수 + 회수일).
import { ActiveCustomersPage } from '@/pages/field/ActiveCustomersPage';
import { CustomerSheetReportsPage } from '@/pages/field/CustomerSheetReportsPage';
import { FaultRemediationsPage } from '@/pages/field/FaultRemediationsPage';
// plan-modusign-style-acceptance §Phase1 — e-Sign template management.
// S3 Phase 3.6 (spec §3.7) — SignTemplatesPage 는 더 이상 라우트가 아니라
// SignDocumentsPage 내부 우측 드로어에서 직접 렌더된다 (여기 import 불필요).
import { SignTemplateDesignerPage } from '@/pages/sign/SignTemplateDesignerPage';
// plan-modusign-style-acceptance §Phase2 — e-Sign document lifecycle.
import { SignDocumentsPage } from '@/pages/sign/SignDocumentsPage';
import { SignDocumentDetailPage } from '@/pages/sign/SignDocumentDetailPage';
// 개선 9건 차수1(26-08-04) §4 — 서명관리 열람 역할 정본(Sidebar 와 같은 값을 쓴다).
import { SIGN_DOCUMENTS_VIEW_ROLES } from '@/pages/sign/sign-access';

// React Router's <Navigate to="..."> does NOT interpolate route params, so a
// literal to="/business/stores/:id" redirects to that literal path (StoreDetailPage
// then 404s on id=":id"). This reads the param and builds the real target.
function StoreIdRedirect(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/business/stores/${id ?? ''}`} replace />;
}

// Annotate the routes array so the inferred router type doesn't
// pull in @remix-run/router types via a deep `.pnpm/...` path
// (TS2742 portability warning).
//
// 개선 9건 차수2(26-08-04) — `export` 는 시험용이다. Sidebar.test.tsx 가 이 표에서
// 라우트의 ProtectedRoute 게이트를 직접 읽어 사이드바 항목의 노출 역할과 맞춘다
// (기대값을 시험에 손으로 적으면 그게 또 하나의 정본이 되어 갈라진다).
// createBrowserRouter 가 만든 router.routes 대신 이 원본 표를 쓴다 — 라이브러리가
// 정규화한 사본은 element 타입이 지워져 있다.
export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <DashboardPage />, handle: { title: '대시보드' } },

      // ─── Business — Customer Master + standalone list pages ──────────
      //
      // Plan v0.3.0 §F-4 (PPTX Slide 6) — BusinessHubPage demoted.
      // The 4-tab hub shell that wrapped DealsKanbanPage / ContractsPage /
      // StoresPage / CustomersPage in v0.2.0 is removed; CustomerMasterPage
      // is now the canonical entry point (sidebar → /business/master).
      //
      // Backward compatibility: each former hub child route is preserved
      // as a flat top-level route so existing deep links / bookmarks
      // (`/business/deals`, `/business/contracts`, `/business/stores`,
      // `/business/customers`) still resolve — they just render the bare
      // list page without the old tab strip. Visiting `/business` itself
      // redirects to /business/master.
      //
      // F-Q2 ✅ — `/contracts/:id`, `/deals/:id`, `/customers/:id` deep
      // routes are unchanged (they live further down at the top level).
      // docs/plan/team-dashboards-and-production-handoff/plan.md §4 — BUSINESS group label = 대시보드.
      { path: 'business', element: <BusinessDashboardPage />, handle: { title: '사업 대시보드' } },
      {
        // round8 (요구 1) — 고객사 마스터 국내(기본). /overseas 는 해외 탭.
        // 2026-10-01 오너 요청 — 상단 바는 '고객사 마스터', 국내/해외는 페이지 제목이 말한다.
        path: 'business/master',
        element: <CustomerMasterPage scope="domestic" />,
        handle: { title: '고객사 마스터' },
      },
      {
        path: 'business/master/overseas',
        element: <CustomerMasterPage scope="overseas" />,
        handle: { title: '고객사 마스터' },
      },
      // Phase 8 — 고객사 서류 (read-only).
      // 권한 통합(오너 결정 2026-08-27) — "권한이 없어서 보지 못했던 탭도 오픈".
      // 종전엔 사업팀(ADMIN/MANAGER/SALES_MANAGER)만 진입할 수 있었다 → 운영 권한 전체.
      // 페이지 본문도 동일 role 가드를 적용 (이중 방어).
      {
        path: 'business/documents',
        element: (
          <ProtectedRoute requiredRoles={OPERATIONS_ROLES}>
            <CustomerDocumentsPage />
          </ProtectedRoute>
        ),
        handle: { title: '고객사 서류' },
      },
      // 2026-10-23 — 구글 시트 '자동 IB(YY.MM)' 달 탭(읽기 전용).
      // 시트 화면 셋은 제조팀이 못 본다(오너 2026-10-23) — BUSINESS_SHEET_VIEW_ROLES(서버 GET 과 같은 상수).
      {
        path: 'business/inbound',
        element: (
          <ProtectedRoute requiredRoles={BUSINESS_SHEET_VIEW_ROLES}>
            <InboundPage />
          </ProtectedRoute>
        ),
        handle: { title: '인바운드 현황(자동)' },
      },
      // 2026-10-23 — 구글 시트 '도입문의 현황 (YYYY_M) 수기' 달 탭(고치기는 운영 권한, 서버가 막는다).
      {
        path: 'business/inquiry',
        element: (
          <ProtectedRoute requiredRoles={BUSINESS_SHEET_VIEW_ROLES}>
            <InquiryPage />
          </ProtectedRoute>
        ),
        handle: { title: '도입 문의 현황(수기)' },
      },
      // 2026-10-23 — 구글 시트 '데모-계약 전환' 탭(고치기는 운영 권한, 서버가 막는다).
      {
        path: 'business/demo-conversion',
        element: (
          <ProtectedRoute requiredRoles={BUSINESS_SHEET_VIEW_ROLES}>
            <DemoConversionPage />
          </ProtectedRoute>
        ),
        handle: { title: '데모-계약 전환' },
      },
      {
        path: 'business/deals',
        element: <DealsKanbanPage />,
        handle: { title: '영업 파이프라인' },
      },
      {
        // 개선 9건 차수2(26-08-04) — 사이드바 BUSINESS 그룹에 '계약' 진입점을 붙였다.
        // 이 라우트는 ProtectedRoute 로 감싸지 않는다(=인증된 전원 열람). 목록 안의
        // 쓰기('새 계약')는 ContractsPage 가 canEditSales 로 감춘다. 게이트를 여기에
        // 추가하면 Sidebar.tsx 의 항목 allowedRoles 도 같이 바꿔야 한다 —
        // Sidebar.test.tsx '사이드바↔라우터 역할 미러링' 시험이 한쪽만 바뀌면 깨진다.
        path: 'business/contracts',
        element: <ContractsPage />,
        handle: { title: '계약' },
      },
      { path: 'business/stores', element: <StoresPage />, handle: { title: '매장' } },
      {
        path: 'business/stores/:id',
        element: <StoreDetailPage />,
        handle: { title: '매장 상세' },
      },
      // redirect for canonical /stores/:id deep links (이미 위 stores/list
      // redirect 패턴과 동일).
      { path: 'stores/:id', element: <StoreIdRedirect /> },
      // docs/plan/customer-master-ux/plan.md — '통합' 결정 (Q6).
      // /business/customers → /business/master 로 redirect. 기존 단일 뷰
      // (v1.0.14 §3) 의 CustomersPage 는 더 이상 라우팅에서 노출되지 않으나
      // 코드는 남겨둠 (Customer detail 페이지가 일부 컴포넌트 재사용 가능성).
      {
        path: 'business/customers',
        element: <Navigate to="/business/master" replace />,
      },

      // docs/plan/team-dashboards-and-production-handoff/plan.md §4 — MANUFACTURING group label = 대시보드.
      // 기존 ManufacturingHubPage (탭 shell) 는 sub-routes 가 dashboard 와 별개 path
      // (`/manufacturing/robots` 등) 로 분리되었으므로 더 이상 hub element 불필요.
      // /manufacturing 자체는 대시보드, 하위는 각자의 페이지.
      {
        path: 'manufacturing',
        element: <ManufacturingDashboardPage />,
        handle: { title: '제조 대시보드' },
      },
      { path: 'manufacturing/robots', element: <RobotsListPage />, handle: { title: '로봇' } },
      {
        path: 'manufacturing/deployments',
        element: <DeploymentsPage />,
        handle: { title: '배치 이력' },
      },
      {
        path: 'manufacturing/production-plan',
        element: <ProductionPlanPage />,
        handle: { title: '제조 계획' },
      },
      {
        path: 'manufacturing/assignments',
        element: <AssignmentInboxPage />,
        handle: { title: '로봇 배정 인박스' },
      },
      // S2 Phase 2.3 — ECN(제조 변경점) 마스터 카탈로그 + 역추적.
      {
        path: 'manufacturing/ecns',
        element: <EcnCatalogPage />,
        handle: { title: '변경점(ECN)' },
      },
      // S2 Phase 2.10 — 재고 Kanban (시트4/5): 구분 사양별 stage 버킷 + Short SN.
      {
        path: 'manufacturing/inventory',
        element: <InventoryKanbanPage />,
        handle: { title: '재고 현황' },
      },
      // docs/plan/loader-history-traceability-2026-07 — 로더 이력·역추적.
      // 정적 경로(quality/revs)를 `:id` 보다 먼저 선언(param 캡처 방지).
      {
        path: 'manufacturing/loaders/quality',
        element: <LoaderQualityPage />,
        handle: { title: '로더 품질' },
      },
      {
        path: 'manufacturing/loader-revs',
        element: <LoaderRevsPage />,
        handle: { title: '로더 개조' },
      },
      {
        path: 'manufacturing/loaders',
        element: <LoadersListPage />,
        handle: { title: '로더 이력' },
      },
      {
        path: 'manufacturing/loaders/:id',
        element: <LoaderDetailPage />,
        handle: { title: '로더 상세' },
      },

      // ─── Detail routes — preserved at the top level ───────────────────
      // The hubs above only host index/list views. Detail pages stay at
      // the existing URLs so deep links / external bookmarks keep working.
      { path: 'robots/:id', element: <RobotDetailPage />, handle: { title: '로봇 상세' } },
      {
        path: 'deals/:id',
        element: <DealDetailPage />,
        handle: { title: '고객사 상세' },
      },
      {
        path: 'contracts/new',
        element: <ContractCreateWizard />,
        handle: { title: '새 계약' },
      },
      // round3 #8f — 계약 상세 복구. 기존 결정 A(상세 미사용 → 고객사 상세 redirect)는
      // 고객사당 계약이 여러 건일 때 2건째부터 어디서도 못 보고, 라인/가격/PDF/해지
      // 상세를 볼 수 없었음. ContractDetailPage 는 이미 완성돼 있어 라우팅만 복구.
      // `:id` = 계약 id. (계약 목록 행 클릭이 이 경로로 이동.)
      {
        path: 'contracts/:id',
        element: <ContractDetailPage />,
        handle: { title: '계약 상세' },
      },
      // §E-1 (Phase 7) — Cross-domain customer detail. Linked from
      // the CustomersPage list rows. Sits at the top level so deep
      // links work the same way as /robots/:id and /deals/:id.
      {
        path: 'customers/:id',
        element: <CustomerDetailPage />,
        handle: { title: '고객사 상세' },
      },

      // v1.1 — MenuOffering matrix editor for a specific store.
      {
        path: 'stores/:id/menu-offerings',
        element: <StoreMenuOfferingsPage />,
        handle: { title: '메뉴 구성' },
      },

      // ─── Legacy redirects ─────────────────────────────────────────────
      // Old top-level list routes redirect into the new hub URLs so
      // browser history + outdated bookmarks still resolve.
      { path: 'deals', element: <Navigate to="/business/deals" replace /> },
      { path: 'contracts', element: <Navigate to="/business/contracts" replace /> },
      { path: 'stores', element: <Navigate to="/business/stores" replace /> },
      { path: 'customers', element: <Navigate to="/business/customers" replace /> },
      { path: 'robots', element: <Navigate to="/manufacturing/robots" replace /> },
      { path: 'deployments', element: <Navigate to="/manufacturing/deployments" replace /> },

      // ─── Field group ──────────────────────────────────────────────────
      // batch-15 §4 — FIELD group label = 대시보드.
      { path: 'field', element: <FieldDashboardPage />, handle: { title: '필드 대시보드' } },
      // batch-15 §3 — Install calendar
      // S3 Phase 3.2 (spec §3.2/§3.4) — 설치 일정 탭 제거. 월간 캘린더는
      // 인프라 발주 페이지 우측으로 흡수됨. 기존 딥링크/북마크 호환을 위해
      // /infrastructure 로 redirect.
      {
        path: 'field/install-calendar',
        element: <Navigate to="/infrastructure" replace />,
      },
      // plan-modusign-style-acceptance §Phase1 — e-Sign template management.
      // S3 Phase 3.6 (spec §3.7) — 서명 양식+발송 통합. 양식 목록은
      // /field/sign/documents 의 우측 드로어로 흡수됨. 기존 딥링크/북마크
      // 호환을 위해 서명 관리 페이지로 redirect.
      {
        path: 'field/sign/templates',
        element: <Navigate to="/field/sign/documents" replace />,
      },
      {
        path: 'field/sign/templates/:id/design',
        element: (
          <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FIELD_MANAGER]}>
            <SignTemplateDesignerPage />
          </ProtectedRoute>
        ),
        handle: { title: '서명 양식 디자이너' },
      },
      // plan-modusign-style-acceptance §Phase2 — e-Sign document lifecycle.
      // 개선 9건 차수1(26-08-04) §4 (결정 ③A) — 제조팀(MFG_MANAGER) 열람 개방.
      // 목록·상세만 열고 **양식 디자이너는 열지 않는다**(위 라우트 — 양식 편집은
      // 쓰기 동선이다). 화면 안의 쓰기 버튼은 페이지가 canWriteSignDocument 로
      // 따로 감춘다. 허용 집합의 정본은 pages/sign/sign-access.ts.
      {
        path: 'field/sign/documents',
        element: (
          <ProtectedRoute requiredRoles={SIGN_DOCUMENTS_VIEW_ROLES}>
            <SignDocumentsPage />
          </ProtectedRoute>
        ),
        // S3 Phase 3.6 — 서명 양식+발송 통합 페이지.
        handle: { title: '서명 관리 · 페리페럴' },
      },
      {
        path: 'field/sign/documents/:id',
        element: (
          <ProtectedRoute requiredRoles={SIGN_DOCUMENTS_VIEW_ROLES}>
            <SignDocumentDetailPage />
          </ProtectedRoute>
        ),
        handle: { title: '서명 발송 상세' },
      },
      {
        // 26-09-11 (오너 요청) — Field 그룹의 독립 '고객사 리포트' 탭.
        //
        // 현장앱 구글 시트에서 수집한 리포트를 보는 **전용 화면**이다
        // (CustomerSheetReport). 서비스 오더 인박스의 '고객 리포트 (접수 원본)'
        // 탭은 QR 스캔분(CustomerReport)이라 **다른 표**다 — 섞지 않는다.
        //
        // 라우트 가드 없음: 형제인 /service-orders · /infrastructure 와 동일하게
        // Field 그룹 공통 접근이고, 수정은 페이지 안에서 canEditField 로 막는다.
        path: 'field/customer-reports',
        element: <CustomerSheetReportsPage />,
        handle: { title: '고객사 리포트' },
      },
      {
        // 고장 조치 이력 — 필드팀이 현장에서 손본 기록('필드팀 유지보수' 시트).
        // 가드는 형제들과 같다(없음): Field 그룹 공통 접근이고, 수정·삭제는
        // 페이지와 서버가 같은 판정(canEditAny / OPERATIONS_ROLES)으로 막는다.
        path: 'field/fault-remediations',
        element: <FaultRemediationsPage />,
        handle: { title: '고장 조치 이력' },
      },
      {
        // round5 #14 — 리포트 인박스는 SO 인박스의 '고객 리포트' 탭으로 이식.
        // 옛 딥링크/알림 호환을 위해 redirect 유지.
        path: 'reports',
        element: <Navigate to="/service-orders?view=reports" replace />,
      },
      {
        path: 'service-orders',
        element: <ServiceOrdersInboxPage />,
        handle: { title: '서비스 오더' },
      },
      {
        // §F-7 — focused triage queue (P1+P2 OPEN, sorted by
        // triageDeadlineAt asc + multi-select bulk actions).
        // S3 Phase 3.1 — 더 이상 독립 라우트가 아닌 서비스 오더 페이지의
        // 내부 탭. 기존 딥링크/북마크 호환을 위해 ?view=triage 로 redirect.
        path: 'service-orders/triage',
        element: <Navigate to="/service-orders?view=triage" replace />,
      },
      {
        path: 'infrastructure',
        element: <InfrastructurePage />,
        handle: { title: '캘린더 · 인프라 발주' },
      },
      // S3 Phase 3.4 (spec §3.5) — 활성 고객사 관리 (운영 중 로봇 보유 고객사
      // roster + 유지보수 등록 + 회수일 확정). 페이지 본문도 write 액션을 role
      // 가드 (VIEWER 는 읽기만) 하므로 라우트 자체는 FIELD 그룹 공통 접근.
      {
        path: 'field/active-customers',
        element: <ActiveCustomersPage />,
        handle: { title: '활성 고객사' },
      },
      // Plan v0.3.0 §J-5 — Fleet Tracker MVP. Sidebar entry is in
      // the Field group (visible to VIEWER+ for housekeeping); the
      // page itself enforces ADMIN / *_MANAGER access in its body
      // so VIEWER users see a friendly "운영 권한 필요" notice
      // rather than empty cards. Server-side §J-1 guards the data.
      { path: 'fleet/tracker', element: <FleetTrackerPage />, handle: { title: '추적 · 분석' } },
      // v0.4.0 §6.3 — LOT 영향권 페이지.
      // windows-ux-edit (2026-06, 슬라이드5) — 제조 사이드바 정리: 부품
      // 카탈로그(parts/catalog)·부품 LOT 리스트(parts/lots)·공급사(suppliers)
      // 라우트 제거(D-4 비파괴: 페이지 컴포넌트·API·DB 보존). LOT 영향권만 유지
      // — 로봇 상세 '부품' 탭의 partLot 링크 대상이라 직접 URL 진입 필요.
      // 백엔드가 ADMIN+MANAGER 만 mutation 허용 → ProtectedRoute 로 진입 제한.
      //
      // 오너 확정 2026-08-20 결정 4 로 복원(제조팀 권한 요구 — 계획서 요구 1).
      // 2026-06 제거와 이번 복원이 겹친 결정 이력이므로 위 주석은 지우지 않는다.
      //
      // ⚠ 오너 결정 2026-08-28 — 사이드바 세 항목(부품 카탈로그·부품 입고·공급사)을
      //   전원에게 숨겼다(layout/nav-hidden.ts). **라우트는 일부러 살려 둔다**:
      //     · 오너가 말한 것은 "탭을 숨겨주세요" 이고 "당장은" 이라는 임시 표현이었다.
      //     · parts/lots/:id(LOT 영향권)는 로봇 상세 '부품' 탭의 LOT 링크가 가는 곳이라
      //       반드시 살아 있어야 한다. 2026-06 에 라우트째 걷어냈다가 바로 이 이유로
      //       되살린 이력이 위에 적혀 있다 — 같은 실수를 반복하지 않는다.
      //     · parts/lots 목록 라우트가 없어지면 LOT 영향권의 뒤로가기 근거
      //       (PartLotImpactPage 의 navigate(-1))와 그 화면 설명이 어긋난다.
      //   즉 여기서 라우트를 지우는 것은 숨김이 아니라 **기능 제거**다. 그건 오너가
      //   따로 결정할 일이다.
      // 서버도 같은 차수에 부품 카탈로그·LOT 리콜·공급사 쓰기를 MFG_MANAGER 에
      // 열었다(parts.controller / suppliers.controller @Roles). 역할 정본은
      // PARTS_SUPPLIERS_ROUTE_ROLES 하나 — 사이드바·아래 4개 라우트가 공유.
      {
        path: 'parts/catalog',
        element: (
          <ProtectedRoute requiredRoles={PARTS_SUPPLIERS_ROUTE_ROLES}>
            <PartCatalogPage />
          </ProtectedRoute>
        ),
        handle: { title: '부품 카탈로그' },
      },
      {
        // 계획서(2026-08-20) 용어 '부품 입고' — 페이지 h1·사이드바와 한 이름.
        path: 'parts/lots',
        element: (
          <ProtectedRoute requiredRoles={PARTS_SUPPLIERS_ROUTE_ROLES}>
            <PartLotsPage />
          </ProtectedRoute>
        ),
        handle: { title: '부품 입고' },
      },
      {
        // 요구1 ⓑ — 종전 [ADMIN, MANAGER] 에 MFG_MANAGER 추가(정본 상수로 교체).
        // 로봇 상세 '부품' 탭의 LOT 링크를 제조팀이 누르면 홈으로 튕기던 구멍.
        path: 'parts/lots/:id',
        element: (
          <ProtectedRoute requiredRoles={PARTS_SUPPLIERS_ROUTE_ROLES}>
            <PartLotImpactPage />
          </ProtectedRoute>
        ),
        handle: { title: 'LOT 영향권' },
      },
      {
        path: 'suppliers',
        element: (
          <ProtectedRoute requiredRoles={PARTS_SUPPLIERS_ROUTE_ROLES}>
            <SuppliersPage />
          </ProtectedRoute>
        ),
        handle: { title: '공급사' },
      },
      {
        path: 'privacy-requests',
        element: (
          <ProtectedRoute requiredRole={UserRole.ADMIN}>
            <PrivacyRequestsInboxPage />
          </ProtectedRoute>
        ),
        handle: { title: 'DSAR 인박스' },
      },
      {
        path: 'audit-logs',
        element: (
          <ProtectedRoute requiredRole={UserRole.ADMIN}>
            <AuditLogsPage />
          </ProtectedRoute>
        ),
        handle: { title: '감사 로그' },
      },
      {
        path: 'users',
        element: (
          <ProtectedRoute requiredRole={UserRole.ADMIN}>
            <UsersPage />
          </ProtectedRoute>
        ),
        handle: { title: '사용자' },
      },
      // Plan v0.4.0 §N-1 / §N-2 — admin tooling pages.
      // Both ADMIN-gated. §N-1 surfaces customers with empty
      // §G-2-optional fields (displayName / legalName); §N-2
      // centralizes OptionGroup picklist management that
      // managers can also do inline via EditableSelect.
      {
        path: 'admin/incomplete-records',
        element: (
          <ProtectedRoute requiredRole={UserRole.ADMIN}>
            <IncompleteRecordsPage />
          </ProtectedRoute>
        ),
        handle: { title: '미입력 인박스' },
      },
      {
        path: 'admin/option-groups',
        element: (
          <ProtectedRoute requiredRole={UserRole.ADMIN}>
            <OptionGroupsAdminPage />
          </ProtectedRoute>
        ),
        handle: { title: '옵션 그룹' },
      },
      // docs/work_log/v1031-remaining-work-batch-12 §3 — Retire 후보 admin page.
      // 90일+ idle 로봇 list (scan-only, 자동 status 변경 없음).
      //
      // 오너 확정 2026-08-20 요구 1 ⑤ — 서버 GET /admin/retire-candidates 가
      // 제조팀(MFG_MANAGER, 레거시 MANAGER superset)에 열리면서(법인 scope 포함)
      // 콘솔 입구도 같은 집합으로 연다 — 서버와 쌍(RETIRE_CANDIDATE_READ_ROLES).
      // 역할 정본은 RETIRE_CANDIDATES_ROUTE_ROLES 하나(사이드바와 공유).
      // 화면은 조회 전용 — 폐기 실행은 결정 1 대로 관리자 전용 유지.
      {
        path: 'admin/retire-candidates',
        element: (
          <ProtectedRoute requiredRoles={RETIRE_CANDIDATES_ROUTE_ROLES}>
            <RetireCandidatesPage />
          </ProtectedRoute>
        ),
        handle: { title: '회수 검토' },
      },
      // round7 §1-7 (D9) — 공개 포털 셀프 체크 위저드 로그(ADMIN read-only).
      {
        path: 'admin/self-check-logs',
        element: (
          <ProtectedRoute requiredRole={UserRole.ADMIN}>
            <SelfCheckLogsPage />
          </ProtectedRoute>
        ),
        handle: { title: '셀프 체크 로그' },
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(routes);
